import {
  Component,
  computed,
  DestroyRef,
  HostBinding,
  inject,
  input,
  InputSignal,
  OnInit,
  Optional,
  output,
  Self,
  Signal,
  signal,
} from '@angular/core';
import {
  MatFormField,
  MatLabel,
  MatSuffix,
} from '@angular/material/form-field';
import {
  MatAutocomplete,
  MatAutocompleteTrigger,
  MatOption,
} from '@angular/material/autocomplete';
import { FormControl, NgControl, ReactiveFormsModule } from '@angular/forms';
import { MatInput } from '@angular/material/input';
import {
  catchError,
  debounceTime,
  forkJoin,
  map,
  switchMap,
  take,
  tap,
} from 'rxjs';
import {
  takeUntilDestroyed,
  toObservable,
  toSignal,
} from '@angular/core/rxjs-interop';
import { ApplyPipe } from '../../apply.pipe';
import {
  defaultDisplayFn,
  DisplayWithSignal,
  QueryFn,
} from './lib/autocomplete';
import { NoopValueAccessorDirective } from '../noop-value-accessor.directive';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { MatIcon } from '@angular/material/icon';
import { getEndpointOf } from '../../../api/api.decorators';

@Component({
  selector: 'app-autocomplete',
  standalone: true,
  imports: [
    MatFormField,
    MatAutocomplete,
    MatOption,
    ReactiveFormsModule,
    MatAutocompleteTrigger,
    MatInput,
    MatLabel,
    ApplyPipe,
    MatIcon,
    RouterLink,
    MatSuffix,
  ],
  hostDirectives: [NoopValueAccessorDirective],
  templateUrl: './autocomplete.component.html',
})
export class AutocompleteComponent<T> implements OnInit {
  @HostBinding('class') class = 'w-full';
  private readonly destroyRef = inject(DestroyRef);
  formControl = new FormControl<T | null>(null);
  inputValue = signal('');
  blur = output();
  panelWidth = input<string | number>();
  // Casting panelWidth above directly causes problems with Angular's static analysis?
  // We remove `| undefined` from the generic so that we can omit panelWidth
  // and pass undefined to inner panelWidth to leave value unset.
  _panelWidth = this.panelWidth as InputSignal<string | number>;

  // Different behavior of autocomplete in the admin portal
  private readonly route = inject(ActivatedRoute);
  protected isAdminPortal = toSignal(
    // Every time the route changes
    this.route.url.pipe(
      switchMap(() => {
        // Take all the route segments
        return forkJoin(
          [...this.route.pathFromRoot, ...this.route.children].map((route) =>
            route.url.pipe(take(1)),
          ),
        );
      }),
      map((urls) => {
        const segments = urls.flat().map((url) => url.path);
        return (
          segments[0] === '' &&
          segments[1] === 'portal' &&
          segments[2] === 'admin'
        );
      }),
    ),
  );

  // Filtered items signal
  loading = signal(false);
  items: Signal<T[]> = toSignal(
    toObservable(this.inputValue).pipe(
      tap(() => this.loading.set(true)),
      debounceTime(250),
      switchMap((search) => {
        return this.queryFn()(search ?? '');
      }),
      tap(() => this.loading.set(false)),
      catchError((err) => {
        this.loading.set(false);
        throw err;
      }),
    ),
    { initialValue: [] },
  );

  // Rendering inputs
  label = input('');
  displayWith: DisplayWithSignal<T> = input(defaultDisplayFn, {
    transform: (fn) => (item: T) => (item ? fn(item) : ''),
  });
  queryFn = input.required<QueryFn<T>>();

  // Go to selected item router link
  selectedItem = signal<T | null>(null);
  routerLink = computed(() => {
    const selectedItem = this.selectedItem();
    const base = getEndpointOf(selectedItem as any);
    const id = (selectedItem as any)?.id;
    if (!selectedItem || !base || !id) return undefined;
    return ['/portal/admin', base, 'details', id];
  });

  constructor(@Optional() @Self() private readonly ngControl: NgControl) {}

  ngOnInit() {
    // Retrieve the passed in ngControl, or use our own default one.
    if (this.ngControl?.control) {
      if (this.ngControl.control instanceof FormControl) {
        this.formControl = this.ngControl.control;
      } else {
        throw new Error(`Autocomplete only supports FormControl.`);
      }
    }
    // Sync form control with selectedItem
    this.selectedItem.set(this.formControl.value);
    this.formControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((v) => this.selectedItem.set(v));
  }

  setInputValue($event: Event | string) {
    this.inputValue.set(
      typeof $event === 'string'
        ? $event
        : ($event.target as HTMLInputElement).value,
    );
  }
  cleared = signal(false);

  selected() {
    this.cleared.set(false);
  }

  closed() {
    // When the control was cleared with the swap button and the autocomplete is
    // closed without selecting an item we reset it to the last value.
    const selectedItem = this.selectedItem();
    if (this.cleared() && selectedItem) {
      this.formControl.setValue(selectedItem);
    }
    this.cleared.set(false);
  }

  handleBlur() {
    this.blur.emit();
  }
}
